import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon, StarIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { nullToUndefined } from '../../lib/utils'
import { ConciergeEventForm } from '../../components/forms/ConciergeEventForm'
import { useTranslation } from '../../hooks/useTranslation'
import type { ConciergeEvent, ConciergeEventInput, ConciergePartner } from '../../../shared/types'

const eventsApi = createCrudApi<ConciergeEvent, ConciergeEventInput>('concierge-events')
const partnersApi = createCrudApi<ConciergePartner, any>('concierge-partners')

export function ConciergeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const { t, locale } = useTranslation()

  const { data: event, isLoading } = useQuery({
    queryKey: ['concierge-events', id],
    queryFn: () => eventsApi.get(id!),
    enabled: !!id,
  })

  const { data: partner } = useQuery({
    queryKey: ['concierge-partners', event?.partnerId],
    queryFn: () => partnersApi.get(event!.partnerId!),
    enabled: !!event?.partnerId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<ConciergeEventInput>) => eventsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['concierge-events', id] })
      queryClient.invalidateQueries({ queryKey: ['concierge-events'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => eventsApi.delete(id!),
    onSuccess: () => {
      navigate('/concierge')
    },
  })

  // Get localized title/description based on current locale
  const getLocalizedTitle = () => {
    if (locale === 'de' && event?.titleDe) return event.titleDe
    return event?.titleEn
  }

  const getLocalizedDescription = () => {
    if (locale === 'de' && event?.descriptionDe) return event.descriptionDe
    return event?.descriptionEn
  }

  if (isLoading) return <div>{t('common.loading')}</div>
  if (!event) return <div>{t('pages.concierge.notFound')}</div>

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/concierge"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.concierge.backToList')}
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            {event.featured && <StarIcon className="h-5 w-5 text-yellow-500" />}
            <h1 className="text-2xl font-bold text-gray-900">{getLocalizedTitle()}</h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">{event.location}</p>
        </div>

        {/* Event Image */}
        {event.imageUrl && (
          <div className="mb-6">
            <img
              src={event.imageUrl}
              alt={getLocalizedTitle()}
              className="w-full rounded-lg"
            />
          </div>
        )}

        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.concierge.confirmDelete'))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.status')}</dt>
              <dd className="mt-1">
                <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                  event.status === 'upcoming' ? 'bg-green-100 text-green-800' :
                  event.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {t(`concierge.status.${event.status}`)}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.category')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{t(`concierge.categories.${event.category}`)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('concierge.fields.visibility')}</dt>
              <dd className="mt-1">
                <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                  event.visibility === 'public' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                }`}>
                  {t(`concierge.visibility.${event.visibility}`)}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('concierge.fields.eventDate')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(event.eventDate).toLocaleString()}
              </dd>
            </div>
            {partner && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('concierge.fields.partner')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{partner.name}</dd>
              </div>
            )}
            {event.capacity && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('concierge.fields.capacity')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{event.capacity}</dd>
              </div>
            )}
            {event.patronCapacity && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('concierge.fields.patronCapacity')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{event.patronCapacity}</dd>
              </div>
            )}
          </dl>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <div className="mb-4">
              <Link
                to="/concierge"
                className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                {t('pages.concierge.backToList')}
              </Link>
            </div>
            <div className="flex gap-x-3">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="inline-flex items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
              >
                <PencilIcon className="h-4 w-4" />
                {isEditing ? t('common.cancel') : t('common.edit')}
              </button>
              <button
                onClick={() => {
                  if (confirm(t('pages.concierge.confirmDelete'))) {
                    deleteMutation.mutate()
                  }
                }}
                className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
              >
                <TrashIcon className="h-4 w-4" />
                {t('common.delete')}
              </button>
            </div>
          </div>

          {/* Detail card */}
          <div className="overflow-hidden bg-white shadow sm:rounded-lg">
            <div className="px-4 py-6 sm:px-6">
              <div className="flex items-center gap-2">
                {event.featured && <StarIcon className="h-5 w-5 text-yellow-500" />}
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{getLocalizedTitle()}</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('pages.concierge.detailSubtitle')}</p>
                </div>
              </div>
            </div>

            {isEditing ? (
              <div className="border-t border-gray-100 px-4 py-6 sm:px-6">
                <ConciergeEventForm
                  defaultValues={{
                    ...nullToUndefined(event),
                    eventDate: new Date(event.eventDate).toISOString().slice(0, 16),
                    partnerId: event.partnerId || undefined,
                  }}
                  onSubmit={(data) => updateMutation.mutate(data)}
                  onCancel={() => setIsEditing(false)}
                  loading={updateMutation.isPending}
                />
              </div>
            ) : (
              <div className="border-t border-gray-100">
                <dl className="divide-y divide-gray-100">
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('concierge.fields.titleEn')}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                      {event.titleEn}
                    </dd>
                  </div>
                  {event.titleDe && (
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-900">{t('concierge.fields.titleDe')}</dt>
                      <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                        {event.titleDe}
                      </dd>
                    </div>
                  )}
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('concierge.fields.location')}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                      {event.location}
                      {event.address && <div className="text-gray-500">{event.address}</div>}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('concierge.fields.eventDate')}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                      {new Date(event.eventDate).toLocaleString()}
                    </dd>
                  </div>
                  {partner && (
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-900">{t('concierge.fields.partner')}</dt>
                      <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                        <div className="flex items-center gap-2">
                          {partner.logoUrl && (
                            <img src={partner.logoUrl} alt={partner.name} className="h-6 w-auto" />
                          )}
                          {partner.name}
                        </div>
                      </dd>
                    </div>
                  )}
                  {event.descriptionEn && (
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-900">{t('concierge.fields.descriptionEn')}</dt>
                      <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0 whitespace-pre-wrap">
                        {event.descriptionEn}
                      </dd>
                    </div>
                  )}
                  {event.descriptionDe && (
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-900">{t('concierge.fields.descriptionDe')}</dt>
                      <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0 whitespace-pre-wrap">
                        {event.descriptionDe}
                      </dd>
                    </div>
                  )}
                  {(event.capacity || event.patronCapacity) && (
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-900">{t('concierge.sections.capacity')}</dt>
                      <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                        {event.capacity && <div>{t('concierge.fields.capacity')}: {event.capacity}</div>}
                        {event.patronCapacity && <div>{t('concierge.fields.patronCapacity')}: {event.patronCapacity}</div>}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
